// 20260111 -- Copilot (Claude Sonnet 4.5) -- Task Sorting Utility
//
// Provides a centralized, type-safe, and efficient sorting mechanism for tasks.
// Consolidates duplicate sorting logic previously scattered across taskOps.js 
// and store.js. Fixes priority sorting to use numeric ranking instead of 
// alphabetical comparison.

// 20260111 -- Copilot (Claude Sonnet 4.5) -- Priority ranking map for proper priority comparison.
// Used to convert priority strings to numeric values for correct sorting
// (e.g., 'high' > 'medium' > 'low', not alphabetically).
const PRIORITY_RANKING = {
  unassigned: -99,
  negative: -1,
  low: 0,
  medium: 1,
  high: 2,
  top: 3
};

// 20260111 -- Copilot (Claude Sonnet 4.5) -- Gets the comparable value for a task field.
// Handles special cases like priority (converts to numeric ranking), date/time
// fields (combines date with time component), and null/undefined values. 
// Returns the raw field value for standard fields.
//
// @param {Object} task - The task object
// @param {string} field - The field name to get value from
// @returns {*} - The comparable value (null if field is undefined/null)
function getComparableValue(task, field) {
  if (!field || typeof task[field] === 'undefined' || task[field] === null) {
    return null;
  }

  // 20260111 -- Copilot (Claude Sonnet 4.5) -- Special handling for priority field - use ranking 
  // instead of alphabetical comparison to ensure proper sort order
  if (field === 'priority') {
    const priorityValue = task[field];
    return PRIORITY_RANKING[priorityValue] ?? PRIORITY_RANKING.unassigned;
  }

  // 20260111 -- Copilot (Claude Sonnet 4.5) -- Special handling for start - use starttime
  // if defined for more precise sorting, otherwise fall back to start date
  if (field === 'start') {
    const startTime = task['starttime'];
    if (startTime && typeof startTime !== 'undefined' && startTime !== null) {
      return startTime;
    }
    return task[field];
  }

  // 20260111 -- Copilot (Claude Sonnet 4.5) -- Special handling for due - use duetime
  // if defined for more precise sorting, otherwise fall back to due date
  if (field === 'due') {
    const dueTime = task['duetime'];
    if (dueTime && typeof dueTime !== 'undefined' && dueTime !== null) {
      return dueTime;
    }
    return task[field];
  }

  return task[field];
}

// 20260111 -- Copilot (Claude Sonnet 4.5) -- Compares two values with proper null handling.
// Nulls are always pushed to the end of the list regardless of sort direction.
// This ensures tasks with missing values don't interfere with the primary sort.
//
// @param {*} a - First value
// @param {*} b - Second value
// @param {number} direction - Sort direction (1 for ascending, -1 for descending)
// @returns {number} - Comparison result (-1, 0, or 1)
function compareValues(a, b, direction) {
  
  // 20260111 -- Copilot (Claude Sonnet 4.5) -- Handle nulls - push to end regardless of direction
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  // 20260111 -- Copilot (Claude Sonnet 4.5) -- Compare values
  if (a > b) return 1 * direction;
  if (a < b) return -1 * direction;
  return 0;
}

// 20260111 -- Copilot (Claude Sonnet 4.5) -- Creates a custom sort comparator function for tasks.
// Supports up to three levels of sorting with independent directions for each level.
// Returns a comparator function suitable for use with Array.sort().
//
// @param {string} first - Primary sort field
// @param {string} second - Secondary sort field (optional)
// @param {string} third - Tertiary sort field (optional)
// @param {number} firstDirection - Primary sort direction (1 or -1)
// @param {number} secondDirection - Secondary sort direction (1 or -1)
// @param {number} thirdDirection - Tertiary sort direction (1 or -1)
// @returns {Function} - Comparator function for Array.sort()
export function createTaskSorter(
  first,
  second = null,
  third = null,
  firstDirection = 1,
  secondDirection = 1,
  thirdDirection = 1
) {
  return function(a, b) {
    
    // 20260111 -- Copilot (Claude Sonnet 4.5) -- Primary comparison
    if (first) {
      const aVal = getComparableValue(a, first);
      const bVal = getComparableValue(b, first);
      const result = compareValues(aVal, bVal, firstDirection);
      if (result !== 0) return result;
    }

    // 20260111 -- Copilot (Claude Sonnet 4.5) -- Secondary comparison
    if (second) {
      const aVal = getComparableValue(a, second);
      const bVal = getComparableValue(b, second);
      const result = compareValues(aVal, bVal, secondDirection);
      if (result !== 0) return result;
    }

    // 20260111 -- Copilot (Claude Sonnet 4.5) -- Tertiary comparison
    if (third) {
      const aVal = getComparableValue(a, third);
      const bVal = getComparableValue(b, third);
      const result = compareValues(aVal, bVal, thirdDirection);
      if (result !== 0) return result;
    }

    return 0;
  };
}

// 20260111 -- Copilot (Claude Sonnet 4.5) -- Sorts an array of tasks in place using the provided
// sort order configuration. Convenience wrapper around createTaskSorter that
// accepts a sortOrder object instead of individual parameters.
//
// @param {Array} tasks - Array of task objects to sort
// @param {Object} sortOrder - Sort order configuration
// @param {string} sortOrder.first - Primary sort field
// @param {string} sortOrder.second - Secondary sort field
// @param {string} sortOrder.third - Tertiary sort field
// @param {number} sortOrder.firstDirection - Primary sort direction
// @param {number} sortOrder.secondDirection - Secondary sort direction
// @param {number} sortOrder.thirdDirection - Tertiary sort direction
// @returns {Array} - The sorted array (same reference as input)
export function sortTasks(tasks, sortOrder) {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return tasks;
  }

  const comparator = createTaskSorter(
    sortOrder.first,
    sortOrder.second,
    sortOrder.third,
    sortOrder.firstDirection,
    sortOrder.secondDirection,
    sortOrder.thirdDirection
  );

  return tasks.sort(comparator);
}

// 20260111 -- Copilot (Claude Sonnet 4.5) -- Default export for convenience
export default {
  createTaskSorter,
  sortTasks,
  PRIORITY_RANKING
};
